import { withSessionToken } from '../../auth';
import { API_URL } from '../../config';

export type AuthHeadersFn = (json?: boolean) => HeadersInit;

interface PhoneSensorCreateResponse {
  sensor: Record<string, unknown>;
  pin: string;
  capture_url: string;
  qr_code: string;
  expires_in_seconds: number;
}

interface PinInfoResponse {
  pin: string;
  capture_url: string;
  qr_code: string;
}

interface ViewerTokenResponse {
  livekit_url: string;
  livekit_token: string;
  room_name: string;
}

export async function createPhoneSensor(
  name: string,
  authHeaders: AuthHeadersFn,
  scope: string = 'group',
  latitude?: number,
  longitude?: number,
): Promise<PhoneSensorCreateResponse> {
  const body: Record<string, unknown> = { name };
  if (latitude !== undefined) body.latitude = latitude;
  if (longitude !== undefined) body.longitude = longitude;
  const resp = await fetch(`${API_URL}/api/phone-sensor/create?scope=${scope}`, {
    method: 'POST',
    headers: authHeaders(true),
    body: JSON.stringify(body),
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ detail: `HTTP ${resp.status}` }));
    throw new Error(err.detail || 'Failed to create phone sensor');
  }
  return resp.json();
}

export async function getPhoneSensorPin(
  sensorId: number,
  authHeaders: AuthHeadersFn,
): Promise<PinInfoResponse> {
  const resp = await fetch(`${API_URL}/api/phone-sensor/${sensorId}/pin`, {
    headers: authHeaders(),
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ detail: `HTTP ${resp.status}` }));
    throw new Error(err.detail || 'Failed to get PIN');
  }
  return resp.json();
}

export async function getViewerToken(
  sensorId: number,
  authHeaders: AuthHeadersFn,
): Promise<ViewerTokenResponse> {
  const resp = await fetch(`${API_URL}/api/phone-sensor/${sensorId}/viewer-token`, {
    method: 'POST',
    headers: authHeaders(true),
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ detail: `HTTP ${resp.status}` }));
    throw new Error(err.detail || 'Failed to get viewer token');
  }
  return resp.json();
}

export async function deletePhoneSensor(
  sensorId: number,
  authHeaders: AuthHeadersFn,
): Promise<void> {
  const resp = await fetch(`${API_URL}/api/phone-sensor/${sensorId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ detail: `HTTP ${resp.status}` }));
    throw new Error(err.detail || 'Failed to delete phone sensor');
  }
}

export async function refreshPhoneSensorGps(
  sensorId: number,
  authHeaders: AuthHeadersFn,
): Promise<void> {
  const resp = await fetch(`${API_URL}/api/phone-sensor/${sensorId}/refresh-gps`, {
    method: 'POST',
    headers: authHeaders(),
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ detail: `HTTP ${resp.status}` }));
    throw new Error(err.detail || 'Failed to refresh GPS');
  }
}

export interface Recording {
  id: number;
  sensor_id: number;
  filename: string;
  file_size: number;
  duration_seconds: number | null;
  created_at: string;
}

export async function uploadRecording(
  sensorId: number,
  blob: Blob,
  duration: number,
  authHeaders: AuthHeadersFn,
): Promise<Recording> {
  const form = new FormData();
  form.append('file', blob, `recording-${Date.now()}.webm`);
  form.append('duration', String(Math.round(duration)));
  const headers = authHeaders(false);
  const resp = await fetch(`${API_URL}/api/phone-sensor/${sensorId}/recordings/upload`, {
    method: 'POST',
    headers,
    body: form,
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ detail: `HTTP ${resp.status}` }));
    throw new Error(err.detail || 'Upload failed');
  }
  return resp.json();
}

export async function listRecordings(
  sensorId: number,
  authHeaders: AuthHeadersFn,
): Promise<Recording[]> {
  const resp = await fetch(`${API_URL}/api/phone-sensor/${sensorId}/recordings`, {
    headers: authHeaders(),
  });
  if (!resp.ok) return [];
  return resp.json();
}

export function getRecordingStreamUrl(recordingId: number): string {
  // A <video src> cannot send a header, so the session rides in the query string. It used
  // to be the shared REACT_APP_API_KEY, which CRA baked into the bundle for every visitor.
  return withSessionToken(`${API_URL}/api/phone-sensor/recordings/${recordingId}/stream`);
}

export async function deleteRecording(
  recordingId: number,
  authHeaders: AuthHeadersFn,
): Promise<void> {
  const resp = await fetch(`${API_URL}/api/phone-sensor/recordings/${recordingId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ detail: `HTTP ${resp.status}` }));
    throw new Error(err.detail || 'Delete failed');
  }
}

export interface TrackPoint {
  lat: number;
  lng: number;
  accuracy: number | null;
  altitude: number | null;
  heading: number | null;
  speed: number | null;
  battery: number | null;
  recorded_at: string;
}

export async function getTrack(
  sensorId: number,
  authHeaders: AuthHeadersFn,
): Promise<TrackPoint[]> {
  const resp = await fetch(`${API_URL}/api/phone-sensor/${sensorId}/track`, {
    headers: authHeaders(),
  });
  if (!resp.ok) return [];
  return resp.json();
}

export function getTrackKmlUrl(sensorId: number): string {
  return `${API_URL}/api/phone-sensor/${sensorId}/track/kml`;
}

export async function clearTrack(
  sensorId: number,
  authHeaders: AuthHeadersFn,
): Promise<void> {
  const resp = await fetch(`${API_URL}/api/phone-sensor/${sensorId}/track`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ detail: `HTTP ${resp.status}` }));
    throw new Error(err.detail || 'Failed to clear track');
  }
}
